import { ColorEnum } from '@/constants/color'
import { Layout } from '@/features/layout/types'
import AmountInput from '@/features/shiftCloseouts/components/AmountInput'
import CashTransactionDepositIcon from '@/features/shiftCloseouts/components/CashTransactionForm/parts/CashTransactionDepositIcon'
import CashTransactionWithdrawalIcon from '@/features/shiftCloseouts/components/CashTransactionForm/parts/CashTransactionWithdrawalIcon'
import { CashTransactionDraft } from '@/features/shiftCloseouts/types'
import Button from '@/features/ui/buttons/components/Button'
import InputField from '@/features/ui/inputs/components/InputField'
import Typography from '@/features/ui/typography'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface CashTransactionFormProps {
    currency: string
    deposit: CashTransactionDraft
    layout: Layout
    withdrawal: CashTransactionDraft
}

const CashTransactionForm: FC<CashTransactionFormProps> = ({
    currency,
    deposit,
    layout,
    withdrawal,
}) => {
    const { t } = useTranslation()
    const { styles } = useStyles(stylesheet, { layout })

    const rows = [
        {
            action: t('shiftCloseouts.cashTransactions.confirmDeposit'),
            amountLabel: t('shiftCloseouts.cashTransactions.depositAmountLabel'),
            draft: deposit,
            icon: <CashTransactionDepositIcon testID="cash-transaction-deposit-icon" />,
            noteLabel: t('shiftCloseouts.cashTransactions.depositNoteLabel'),
            notePlaceholder: t('shiftCloseouts.cashTransactions.deposit'),
            testID: 'shift-closeout-cash-transaction-deposit-row',
        },
        {
            action: t('shiftCloseouts.cashTransactions.confirmWithdrawal'),
            amountLabel: t('shiftCloseouts.cashTransactions.withdrawalAmountLabel'),
            draft: withdrawal,
            icon: <CashTransactionWithdrawalIcon testID="cash-transaction-withdrawal-icon" />,
            noteLabel: t('shiftCloseouts.cashTransactions.withdrawalNoteLabel'),
            notePlaceholder: t('shiftCloseouts.cashTransactions.withdrawal'),
            testID: 'shift-closeout-cash-transaction-withdrawal-row',
        },
    ]

    return (
        <View style={styles.container}>
            <Typography.Heading variant="h5">
                {t('shiftCloseouts.cashTransactions.addHeading')}
            </Typography.Heading>

            <View style={styles.rows}>
                {rows.map(
                    ({ action, amountLabel, draft, icon, noteLabel, notePlaceholder, testID }) => (
                        <View key={noteLabel} style={styles.row} testID={testID}>
                            <View
                                pointerEvents="none"
                                style={styles.rowBorder}
                                testID={`${testID}-border`}
                            />
                            <View style={styles.noteGroup}>
                                <InputField
                                    accessibilityLabel={noteLabel}
                                    FrontComponent={icon}
                                    inputStyle={styles.noteTextInput}
                                    onChange={draft.onNoteChange}
                                    placeholder={notePlaceholder}
                                    style={styles.noteInput}
                                    value={draft.note}
                                />
                            </View>
                            <View style={styles.amountGroup}>
                                <AmountInput
                                    accessibilityLabel={amountLabel}
                                    appearance="subtle"
                                    currency={currency}
                                    onChange={draft.onAmountChange}
                                    style={styles.amountInput}
                                    value={draft.amount}
                                />
                                <Button
                                    disabled={!draft.canSubmit}
                                    onPress={draft.onSubmit}
                                    style={styles.action}
                                >
                                    {action.toUpperCase()}
                                </Button>
                            </View>
                        </View>
                    )
                )}
            </View>
        </View>
    )
}

const stylesheet = createStyleSheet((theme) => ({
    container: {
        gap: 16,
    },
    rows: {
        gap: 8,
    },
    row: {
        gap: 8,
        padding: 8,
        borderRadius: 12,
        backgroundColor: ColorEnum.VIOLET_100,
        variants: {
            layout: {
                compact: {
                    alignItems: 'stretch',
                },
                extended: {
                    flexDirection: 'row',
                    alignItems: 'center',
                },
            },
        },
    },
    rowBorder: {
        ...StyleSheet.absoluteFillObject,
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: ColorEnum.VIOLET_200,
        borderRadius: 12,
    },
    noteGroup: {
        flex: 1,
        minWidth: 0,
    },
    amountGroup: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 8,
        variants: {
            layout: {
                compact: {
                    width: '100%',
                },
                extended: {
                    flexShrink: 0,
                },
            },
        },
    },
    noteInput: {
        width: 'auto',
        height: 40,
        flex: 1,
        paddingHorizontal: 16,
    },
    noteTextInput: {
        paddingHorizontal: 0,
        fontFamily: theme.fonts.medium,
        fontSize: 14,
        lineHeight: 16,
    },
    amountInput: {
        width: 124,
    },
    action: {
        justifyContent: 'center',
        borderRadius: 8,
        variants: {
            layout: {
                compact: {
                    flex: 1,
                    minWidth: 0,
                },
                extended: {
                    width: 180,
                },
            },
        },
    },
}))

export default CashTransactionForm
